import { opaqueId } from './groupTripModel';
import { travelerPresentation } from './disruptionModel';
import { memberIdentityShortTag } from '../member-identity/memberIdentityPresentation';

/**
 * D5 處理協議（Settlement Agreement）前端模型。
 * P0 只記錄當事人之間的處理協議，不移動任何款項；所有文案不得暗示退款、付款或撥款已完成。
 * Snowflake ID 與金額全程以字串處理，禁止轉為 JavaScript Number。
 */

export const SETTLEMENT_STATUS_COPY = {
  DRAFT: '草稿',
  AWAITING_CONFIRMATIONS: '等待必要當事人確認',
  READY_TO_RECORD: '準備記錄協議',
  RECORDED: '協議已記錄',
  REVISION_REQUIRED: '需要修訂',
  WITHDRAWN: '已撤回',
};

export const CONSEQUENCE_TYPE_COPY = {
  REFUND: '退款協議',
  CHANGE_FEE: '改票／變更費用',
  NEW_TICKET: '新票費用',
  LOST_DISCOUNT: '優惠損失',
  ACCOMMODATION_PENALTY: '住宿違約／取消費',
  OTHER: '其他',
};

/** 協議方向只描述當事人「應」承擔或取得的內容，不是帳務交易。 */
export const DIRECTION_COPY = {
  PRINCIPAL_RECEIVES: '應取得',
  PRINCIPAL_PAYS: '應負擔',
  ENTITLEMENT_WAIVED: '放棄權益',
};

export const PRINCIPAL_ROLE_COPY = {
  PAYER: '付款相關當事人',
  BENEFICIARY: '權益相關當事人',
};

export const CONSENT_STATE_COPY = {
  PENDING: '尚未回覆',
  CONFIRMED: '已確認',
  REJECTED: '已拒絕',
};

export const IMPACT_SOURCE_COPY = {
  SUPPLIER_CONFIRMATION: '供應商確認',
};

export const CONSEQUENCE_TYPES = Object.keys(CONSEQUENCE_TYPE_COPY);
export const DIRECTIONS = Object.keys(DIRECTION_COPY);

export const RECORDED_NOTE = '此狀態代表相關當事人已完成本次處理協議確認；實際退款、付款或其他金流處理不屬於此狀態。';
export const REVISION_NOTE = '修訂後會建立新的版本，先前的確認不會延續，所有必要當事人需要重新確認。';

const copyOf = (table, fallback) => (value) => (
  Object.prototype.hasOwnProperty.call(table, value) ? table[value] : fallback
);

export const settlementStatusLabel = copyOf(SETTLEMENT_STATUS_COPY, '狀態未提供');
export const consequenceTypeLabel = copyOf(CONSEQUENCE_TYPE_COPY, '其他');
export const directionLabel = copyOf(DIRECTION_COPY, '協議方向未提供');
export const principalRoleLabel = copyOf(PRINCIPAL_ROLE_COPY, '相關當事人');
export const consentStateLabel = copyOf(CONSENT_STATE_COPY, '尚未回覆');
export const impactSourceLabel = copyOf(IMPACT_SOURCE_COPY, '已記錄的確認紀錄');

export const isMonetaryDirection = (direction) => direction === 'PRINCIPAL_RECEIVES' || direction === 'PRINCIPAL_PAYS';

const REVISABLE = new Set(['DRAFT', 'REVISION_REQUIRED']);
const WITHDRAWABLE = new Set(['DRAFT', 'AWAITING_CONFIRMATIONS', 'REVISION_REQUIRED']);
export const isRevisable = (status) => REVISABLE.has(status);
export const isWithdrawable = (status) => WITHDRAWABLE.has(status);
export const isSubmittable = (status) => status === 'DRAFT';

const idList = (value) => (Array.isArray(value) ? value.map(opaqueId).filter(Boolean) : []);
const text = (value) => (typeof value === 'string' ? value : value == null ? '' : String(value));

/** rowVersion 為後端 Long（字串輸出），原樣保留為字串並原樣送回，不做數值運算。 */
export function normalizeSettlementProposal(value) {
  const snapshot = value?.impactSnapshot;
  return {
    proposalId: opaqueId(value?.proposalId),
    groupTripId: opaqueId(value?.groupTripId),
    caseId: opaqueId(value?.caseId),
    bookingId: opaqueId(value?.bookingId),
    creatorMemberId: opaqueId(value?.creatorMemberId),
    status: value?.status || 'DRAFT',
    generation: Number.isInteger(value?.generation) ? value.generation : 0,
    rowVersion: opaqueId(value?.rowVersion),
    impactSnapshot: snapshot ? {
      impactScope: snapshot.impactScope || 'UNKNOWN_PENDING_CONFIRMATION',
      affectedTravelerIds: idList(snapshot.affectedTravelerIds),
      confirmedAt: snapshot.confirmedAt || null,
      sourceEventType: snapshot.sourceEventType || '',
    } : null,
    consequences: (Array.isArray(value?.consequences) ? value.consequences : []).map((item) => ({
      consequenceId: opaqueId(item?.consequenceId),
      displayOrder: Number.isInteger(item?.displayOrder) ? item.displayOrder : 0,
      type: item?.type || 'OTHER',
      direction: item?.direction || '',
      principalRole: item?.principalRole || '',
      principalMemberId: opaqueId(item?.principalMemberId),
      principalTravelerId: opaqueId(item?.principalTravelerId),
      amount: item?.amount == null ? null : text(item.amount),
      currency: item?.currency == null ? null : text(item.currency),
      description: text(item?.description),
      evidenceReference: text(item?.evidenceReference),
    })).sort((a, b) => a.displayOrder - b.displayOrder),
    resolvedPrincipals: (Array.isArray(value?.resolvedPrincipals) ? value.resolvedPrincipals : []).map((item) => ({
      role: item?.role || '',
      memberId: opaqueId(item?.memberId),
      travelerId: opaqueId(item?.travelerId),
    })),
    requiredPrincipals: (Array.isArray(value?.requiredPrincipals) ? value.requiredPrincipals : []).map((item) => ({
      role: item?.role || '',
      memberId: opaqueId(item?.memberId),
      consequenceIds: idList(item?.consequenceIds),
      state: item?.state || 'PENDING',
      decidedAt: item?.decidedAt || null,
    })),
    actorConsent: {
      requiredPrincipal: value?.actorConsent?.requiredPrincipal === true,
      canDecide: value?.actorConsent?.canDecide === true,
      pendingRoles: Array.isArray(value?.actorConsent?.pendingRoles) ? value.actorConsent.pendingRoles : [],
    },
    createdAt: value?.createdAt || null,
    updatedAt: value?.updatedAt || null,
  };
}

/** 確認進度只依後端 requiredPrincipals 計算筆數，不在前端推論是否可記錄。 */
export function consentProgress(proposal) {
  const rows = proposal?.requiredPrincipals || [];
  return {
    total: rows.length,
    confirmed: rows.filter((row) => row.state === 'CONFIRMED').length,
    rejected: rows.filter((row) => row.state === 'REJECTED').length,
  };
}

/** 本人可回覆的項目：只取指向本人、且角色仍待回覆的協議項目。 */
export function actorRelevantConsequences(proposal, viewerId) {
  const viewer = opaqueId(viewerId);
  if (!viewer || !proposal?.actorConsent?.canDecide) return [];
  const pending = new Set(proposal.actorConsent.pendingRoles);
  return proposal.consequences.filter((item) => item.principalMemberId === viewer && pending.has(item.principalRole));
}

/** 管理操作（送出／修訂／撤回）只對提案者或 Host 顯示；僅為介面便利，權限仍以後端為準。 */
export function canManageProposal(proposal, viewerId, hostMemberId) {
  const viewer = opaqueId(viewerId);
  if (!viewer || !proposal) return false;
  return viewer === proposal.creatorMemberId || viewer === opaqueId(hostMemberId);
}

/**
 * 當事人顯示名稱：本人顯示「你」；其他人優先使用旅伴名冊名稱，再退回一般稱呼與短編號。
 * memberId 一律以字串比對。
 */
export function principalPresentation(memberId, { viewerId, proposal, travelersById, travelers } = {}) {
  const member = opaqueId(memberId);
  const reference = member ? `成員編號 …${memberIdentityShortTag(member)}` : '';
  if (member && member === opaqueId(viewerId)) return { primary: '你', reference };
  const resolved = (proposal?.resolvedPrincipals || []).find((item) => item.memberId === member && item.travelerId);
  const consequence = (proposal?.consequences || []).find((item) => item.principalMemberId === member && item.principalTravelerId);
  const linked = (Array.isArray(travelers) ? travelers : []).find((item) => opaqueId(item?.linkedMemberId) === member);
  const travelerId = resolved?.travelerId || consequence?.principalTravelerId || opaqueId(linked?.travelerId);
  if (travelerId) return { primary: travelerPresentation(travelerId, travelersById).primary, reference };
  return { primary: '旅伴', reference };
}

const AMOUNT_PATTERN = /^\d{1,24}(\.\d{1,8})?$/;

/** 金額只做字串格式驗證（最多 24 位整數、8 位小數、必須大於 0），不經過浮點數。 */
export function validateAmountText(value) {
  const raw = text(value).trim();
  if (!raw) return '請輸入金額。';
  if (!AMOUNT_PATTERN.test(raw)) return '金額格式不正確：只接受數字，最多 8 位小數。';
  if (!/[1-9]/.test(raw)) return '金額必須大於 0。';
  return '';
}

/** 以字串加上千分位，保留原始小數位數；不做任何數值換算或匯率。 */
export function formatAmountText(amount) {
  const raw = text(amount).trim();
  if (!/^\d+(\.\d+)?$/.test(raw)) return raw;
  const [integer, fraction] = raw.split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction == null ? grouped : `${grouped}.${fraction}`;
}

export function consequenceAmountText(item) {
  if (item?.amount == null) return '';
  return `${formatAmountText(item.amount)}${item.currency ? ` ${item.currency}` : ''}`;
}

/** 編輯列：金額、幣別與受益人 id 皆為字串。 */
export function emptyConsequenceDraft() {
  return {
    type: 'REFUND', direction: 'PRINCIPAL_RECEIVES', beneficiaryId: '', amount: '', currency: '',
    description: '', evidenceReference: '',
  };
}

/** 修訂時由既有項目回填；受益人以 travelerId 對應回目前訂位的受益人 id，找不到時要求重新選擇。 */
export function draftsFromProposal(proposal, booking) {
  const beneficiaries = Array.isArray(booking?.beneficiaries) ? booking.beneficiaries : [];
  return proposal.consequences.map((item) => {
    const match = item.principalRole === 'BENEFICIARY' && item.principalTravelerId
      ? beneficiaries.filter((row) => opaqueId(row?.travelerId) === item.principalTravelerId) : [];
    return {
      type: item.type,
      direction: item.direction,
      beneficiaryId: match.length === 1 ? opaqueId(match[0].beneficiaryId) : '',
      amount: item.amount || '',
      currency: item.currency || '',
      description: item.description || '',
      evidenceReference: item.evidenceReference || '',
    };
  });
}

/** 前端只做格式檢查；主體解析、權限與 D4 影響一律以後端判定為準。 */
export function validateConsequenceDrafts(drafts) {
  const errors = (drafts || []).map((draft) => {
    const problems = {};
    if (!CONSEQUENCE_TYPES.includes(draft.type)) problems.type = '請選擇項目類型。';
    if (!DIRECTIONS.includes(draft.direction)) problems.direction = '請選擇協議方向。';
    if (draft.type === 'OTHER' && !text(draft.description).trim()) problems.description = '「其他」項目必須填寫說明。';
    if (isMonetaryDirection(draft.direction)) {
      const amountError = validateAmountText(draft.amount);
      if (amountError) problems.amount = amountError;
      const currency = text(draft.currency).trim();
      if (!currency) problems.currency = '請輸入幣別代碼。';
      else if (!/^[A-Za-z0-9_.-]{1,16}$/.test(currency)) problems.currency = '幣別代碼最多 16 個英數字元。';
      if (!text(draft.evidenceReference).trim()) problems.evidenceReference = '金額項目必須提供依據或參考編號。';
    } else if (draft.direction === 'ENTITLEMENT_WAIVED' && !opaqueId(draft.beneficiaryId)) {
      problems.beneficiaryId = '請選擇放棄權益的受益人。';
    }
    return problems;
  });
  const monetaryCurrencies = new Set((drafts || [])
    .filter((draft) => isMonetaryDirection(draft.direction) && text(draft.currency).trim())
    .map((draft) => text(draft.currency).trim()));
  return {
    rows: errors,
    form: !drafts?.length ? '至少需要一個協議項目。'
      : monetaryCurrencies.size > 1 ? '同一份協議只能使用一種幣別。' : '',
    valid: Boolean(drafts?.length) && monetaryCurrencies.size <= 1
      && errors.every((problems) => Object.keys(problems).length === 0),
  };
}

/**
 * 轉成後端 ConsequenceRequest：金額項目一律指向訂位付款人（PAYER），放棄權益指向受益人（BENEFICIARY）。
 * 金額以字串送出，由後端以 BigDecimal 解析；不帶 memberId，當事人由後端解析。
 */
export function toConsequenceRequests(drafts) {
  return drafts.map((draft) => {
    const monetary = isMonetaryDirection(draft.direction);
    const description = text(draft.description).trim();
    const evidence = text(draft.evidenceReference).trim();
    return {
      type: draft.type,
      direction: draft.direction,
      principalRole: monetary ? 'PAYER' : 'BENEFICIARY',
      beneficiaryId: monetary ? null : opaqueId(draft.beneficiaryId),
      amount: monetary ? text(draft.amount).trim() : null,
      currency: monetary ? text(draft.currency).trim() : null,
      description: description || null,
      evidenceReference: evidence || null,
    };
  });
}

const SETTLEMENT_ERRORS = {
  95100: '請求內容不完整，請檢查後再送出。',
  95140: '找不到此處理協議。',
  95141: '找不到相關訂位。',
  95142: '你沒有執行這項操作的權限。',
  95143: '此訂位的行程異動影響尚未確認，暫時無法建立處理協議。',
  95144: '此訂位經確認不受影響，不需要建立處理協議。',
  95145: '行程異動影響已變更，無法以舊的影響內容繼續處理此協議。',
  95146: '受影響旅伴與此案件或訂位不符。',
  95147: '無法確認相關當事人，請先確認訂位的付款人與受益人資料。',
  95148: '此類多人金額分攤目前尚未支援，無法為權益相關當事人指定金額。',
  95149: '協議項目內容不完整或不正確，請檢查類型、方向、金額、幣別與依據。',
  95150: '同一份協議只能使用一種幣別。',
  95151: '協議已由其他人更新，已重新載入最新內容；請確認後再操作。',
  95152: '協議已修訂為新版本，已重新載入最新內容；請確認後再操作。',
  95153: '此訂位已有進行中的處理協議。',
  95154: '這次請求與先前相同識別碼的請求內容不一致，系統沒有執行；請確認最新內容後重新操作。',
  95155: '目前狀態不允許修訂此協議。',
  95156: '目前狀態不允許送出此協議。',
  95157: '你不是此協議的必要當事人，無法確認或拒絕。',
  95158: '你已回覆過，或目前狀態不接受確認／拒絕；已重新載入最新內容。',
  95159: '目前狀態不允許撤回此協議。',
};

/** 收到這些錯誤後必須重新讀取後端權威狀態；不得以舊的版本號自動重送。 */
export const SETTLEMENT_RELOAD_CODES = new Set([95145, 95151, 95152, 95154, 95155, 95156, 95158, 95159]);

export function settlementError(cause) {
  const raw = cause?.code ?? cause?.raw?.code;
  const code = typeof raw === 'number' ? raw : /^\d+$/.test(text(raw)) ? Number(raw) : null;
  const httpStatus = cause?.httpStatus ?? null;
  let message = code != null ? SETTLEMENT_ERRORS[code] : undefined;
  if (!message && httpStatus === 401) message = '請先登入後再操作。';
  return {
    code,
    message: message || '目前無法完成操作，請稍後再試。',
    reload: code != null && SETTLEMENT_RELOAD_CODES.has(code),
    /** 有業務錯誤碼代表伺服器已明確處理並拒絕；沒有代表結果未知（例如網路中斷），重試必須沿用同一個識別碼。 */
    definitive: code != null,
  };
}

export function createSettlementActionKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `settlement-${crypto.randomUUID()}`;
  }
  return `settlement-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Idempotency-Key 管理：同一個操作意圖（動作＋提案＋版本＋內容）在結果未知時重試沿用同一個 key；
 * 成功或伺服器明確拒絕後才釋放，下一次使用者操作才會取得新 key。不會為了繞過衝突而自動換 key 重送。
 */
export function createSettlementKeyStore(factory = createSettlementActionKey) {
  const keys = new Map();
  return {
    keyFor(signature) {
      if (!keys.has(signature)) keys.set(signature, factory());
      return keys.get(signature);
    },
    release(signature) { keys.delete(signature); },
    has(signature) { return keys.has(signature); },
  };
}

export function actionSignature(action, proposal, payload) {
  return [action, proposal?.proposalId || 'new', proposal?.rowVersion || '-', proposal?.generation ?? '-',
    JSON.stringify(payload ?? null)].join('|');
}

export function settlementPath(groupTripId, proposalId) {
  return proposalId ? `/group-trips/${groupTripId}/settlements/${proposalId}` : `/group-trips/${groupTripId}/settlements`;
}
